import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import supportApiService from '../../services/supportApiService';

const SupportTickets = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState(['ASSIGNED', 'RESOLVE', 'INPROGRESS']);
  const [totalTickets, setTotalTickets] = useState(0);

  const availableStatuses = [
    { value: 'ASSIGNED', label: 'Assigned', color: 'bg-blue-100 text-blue-800' },
    { value: 'RESOLVE', label: 'Resolve', color: 'bg-green-100 text-green-800' },
    { value: 'INPROGRESS', label: 'In Progress', color: 'bg-yellow-100 text-yellow-800' }
  ];

  useEffect(() => {
    loadSupportTickets();
  }, [selectedStatuses]);

  useEffect(() => {
    filterTickets();
  }, [tickets, searchTerm]);

  const loadSupportTickets = async () => {
    try {
      setLoading(true);
      console.log('🏨 [SUPPORT-TICKETS] Loading support tickets with statuses:', selectedStatuses);
      
      const response = await supportApiService.getSupportTickets(selectedStatuses);
      
      if (response.success) {
        console.log('✅ Support tickets loaded:', response);
        setTickets(response.tickets || []);
        setTotalTickets(response.totalTickets || 0);
        toast.success('Support tickets loaded successfully');
      } else {
        console.error('❌ Failed to load support tickets:', response.message);
        toast.error(response.message || 'Failed to load support tickets');
      }
    } catch (error) {
      console.error('❌ Error loading support tickets:', error);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    let filtered = tickets;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(ticket =>
        ticket.ticketId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.agentId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.agentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.agentEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.agentQuery?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.truncatedQuery?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredTickets(filtered);
  };

  const handleStatusChange = (status) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const handleTicketClick = (ticketId) => {
    navigate(`/support/dashboard/support-tickets/${ticketId}`);
  };

  const getStatusBadge = (status) => {
    const statusConfig = availableStatuses.find(s => s.value === status);
    const colorClass = statusConfig?.color || 'bg-gray-100 text-gray-800';
    const label = statusConfig?.label || status;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
          <p className="text-gray-600">Manage and track support tickets from agents</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <i className="fas fa-ticket-alt text-green-600"></i>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Tickets</p>
                <p className="text-lg font-semibold text-gray-900">{totalTickets}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <i className="fas fa-user-check text-blue-600"></i>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Assigned</p>
                <p className="text-lg font-semibold text-gray-900">
                  {tickets.filter(t => t.ticketStatus === 'ASSIGNED').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <i className="fas fa-clock text-yellow-600"></i>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-lg font-semibold text-gray-900">
                  {tickets.filter(t => t.ticketStatus === 'INPROGRESS').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <i className="fas fa-check-circle text-green-600"></i>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Resolved</p>
                <p className="text-lg font-semibold text-gray-900">
                  {tickets.filter(t => t.ticketStatus === 'RESOLVE').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            {/* Status Filters */}
            <div className="flex flex-wrap gap-4">
              <span className="text-sm font-medium text-gray-700">Filter by Status:</span>
              {availableStatuses.map(status => (
                <label key={status.value} className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    checked={selectedStatuses.includes(status.value)}
                    onChange={() => handleStatusChange(status.value)}
                  />
                  <span className="ml-2 text-sm text-gray-700">{status.label}</span>
                </label>
              ))}
            </div>

            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Support Tickets Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Support Tickets Details</h3>
            <p className="text-sm text-gray-600">
              Showing {filteredTickets.length} of {tickets.length} tickets
            </p>
          </div>

          {filteredTickets.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-ticket-alt text-gray-400 text-2xl"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Support Tickets Found</h3>
              <p className="text-gray-600">
                {searchTerm || selectedStatuses.length === 0
                  ? 'No tickets match your current filters.' 
                  : 'No support tickets have been created yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ticket ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agent ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agent Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agent Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agent Query
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time Stamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTickets.map((ticket, index) => (
                    <tr 
                      key={ticket.id || index} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleTicketClick(ticket.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-green-600 hover:text-green-800">
                          {ticket.ticketId || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{ticket.agentId || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{ticket.agentName || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{ticket.agentEmail || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={ticket.agentQuery}>
                          {ticket.truncatedQuery || ticket.agentQuery || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(ticket.createdDate)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(ticket.ticketStatus)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportTickets;